// Think-GAS HES Dashboard

import { tokenInstance } from "../baseApi"
import axios from "axios"
import api_url from "../apiPaths"

export const createCancelToken = (cancelApi) => {
  if (cancelApi.current) {
    cancelApi.current.cancel("cancelled")
  }
  const CancelToken = axios.CancelToken
  cancelApi.current = CancelToken.source()
  return cancelApi.current.token
}

/**
 * Latest meter data: POST /hes/api/v1/meter-data/latest
 *
 * `ga` and `district` are body fields, as `string[]` — they used to be
 * comma-separated query params. Everything else about the call is unchanged.
 *
 * Callers pass the meter list as `data.meter`; the wire key is the plural
 * `meters`, same as the periodic endpoint below.
 */
export const getGasMeterProfileData = (body, cancelApi) => {
  const cancelToken = createCancelToken(cancelApi)
  const { data, params } = body
  const requestBody = {
    meters: data?.meter || [],
    ca_no: data?.ca_no || [],
    ga: data?.ga || [],
    district: data?.district || [],
    // Single-valued, and omitted entirely when unset rather than sent empty —
    // the list filters are all opt-in, and an empty string here would read as
    // a status to match rather than "any".
    ...(data?.meter_status ? { meter_status: data.meter_status } : {}),
    ...(data?.communication_status
      ? { communication_status: data.communication_status }
      : {})
  }
  const urlParams = params || {}
  return tokenInstance.post(`${api_url.thinkGasHes}/api/v1/meter-data/latest`, requestBody, { params: urlParams, cancelToken })
}

/**
 * Periodic meter data: POST /hes/api/v1/meter-data/periodic
 *
 * Both identifiers travel in the body, both as `string[]` — the same contract
 * `/meter-data/latest` above takes. `ca_no` used to be comma-joined onto the
 * query string by the list screen while the body carried no `ca_no` slot at
 * all, and the meter detail tab sent no CA at all; a request could therefore
 * name a meter and a CA that had nothing to do with each other.
 *
 * Callers pass the meter list as `data.meter`; the wire key is the plural
 * `meters`, matching the latest endpoint.
 */
export const getGasMeterPushData = (body, cancelApi) => {
  const cancelToken = createCancelToken(cancelApi)
  const { data, params } = body
  const requestBody = {
    meters: data?.meter || [],
    ca_no: data?.ca_no || []
  }
  const urlParams = params || {}
  return tokenInstance.post(`${api_url.thinkGasHes}/api/v1/meter-data/periodic`, requestBody, { params: urlParams, cancelToken })
}

const METER_LIST_URL = `${api_url.thinkGasHes}/api/v1/gas/meter-list/`

/**
 * Command execution reads from this variant instead. Same contract either way
 * — only the set of meters it draws from differs — so the two share every
 * caller below.
 *
 * The trailing slash is required: without it the server answers 307 and the
 * browser reissues the request against the canonical URL, which is a wasted
 * round trip at best.
 */
const METER_LIST_ALL_URL = `${api_url.thinkGasHes}/api/v1/gas/meter-list/all/`

/**
 * Meter / CA typeahead source (new API): POST /hes/api/v1/gas/meter-list/
 * (or `/meter-list/all` with `allMeters`, which command execution uses).
 *
 * Body: `{ paste_values, selected_meters, ga, district }` — `ga` and
 * `district` are `string[]` here; they used to be comma-separated query
 * params. Search terms still travel as query params (`search_by` +
 * `search_query`), which the response echoes back in `data.search_by`.
 *
 * Response: `{ data: { mode, selected_meters, data: [...], search_by,
 * not_found, not_found_count } }` — rows carry `meter_no: { code, id }`
 * alongside flat `ca_no` / `bp_no` / `ga` fields.
 *
 * @param {{params?: object, data?: object, allMeters?: boolean}} body
 */
export const getGasMeterList = (body, cancelApi) => {
  const cancelToken = createCancelToken(cancelApi)
  const { params, data, allMeters = false } = body ?? {}
  return tokenInstance.post(
    allMeters ? METER_LIST_ALL_URL : METER_LIST_URL,
    data ?? { paste_values: [], selected_meters: [] },
    { params: params ?? {}, cancelToken }
  )
}

/**
 * Command execution (new API): POST /hes/api/v1/commands/execute
 *
 * Body: `{ meters: [<int>], command: "SET_ZONE_LIMIT", args: { value, order_id,
 * reason } }` — `meters` are integer meter numbers and `args.value` is whatever
 * the command's descriptor asked for (a scalar, or an object keyed by field).
 *
 * Response: `{ data: { status, message } }`.
 */
export const postCommandExecute = (body, cancelApi) => {
  const cancelToken = createCancelToken(cancelApi)
  const { data } = body ?? {}
  return tokenInstance.post(`${api_url.thinkGasHes}/api/v1/commands/execute`, data ?? {}, {
    cancelToken
  })
}

/**
 * Command catalogue (new API): GET /hes/api/v1/commands/info
 *
 * Called unfiltered — the response carries the whole catalogue grouped by
 * command type, so one request supplies both the category list and each
 * category's commands:
 *   `{ data: [ { label, options: [ { label, value, command_type, input_type,
 *      params } ] } ] }`
 *
 * Each group's `label` is the command type (GET / SET / LOG / CONTROL). The
 * endpoint also accepts a `client_type` filter, but the UI no longer sends one:
 * filtering per category would mean a request per dropdown change, and would
 * pin the category list in the client rather than reading it from the API.
 *
 * `input_type` + `params` describe the form each command needs — see
 * `commandInfo.js`, which turns them into fields.
 */
export const getCommandInfo = (body, cancelApi) => {
  const cancelToken = createCancelToken(cancelApi)
  const { params } = body ?? {}
  return tokenInstance.get(`${api_url.thinkGasHes}/api/v1/commands/info`, {
    params: params ?? {},
    cancelToken
  })
}

/**
 * Cascading filter lists (new API): GET /hes/api/v1/filter-lists-view
 *
 * Same endpoint the dashboard global filters use — only the path moved from
 * `/api/gas_reports/gas/filter-lists-view/`; the response is unchanged:
 *   `{ data: { result: { spv, ga, district, ca_no, sr_no } } }`
 * each a flat array of strings.
 *
 * Query params `spv` / `ga` / `district` / `ca_no` narrow the lists below the
 * level you pass; `project` is a header.
 */
export const getHesFilterLists = (body, cancelApi) => {
  const cancelToken = createCancelToken(cancelApi)
  const { params } = body ?? {}
  return tokenInstance.get(`${api_url.thinkGasHes}/api/v1/filter-lists-view`, {
    params: params ?? {},
    cancelToken
  })
}

const CMD_HISTORY_URL = `${api_url.thinkGasHes}/api/v1/commands/history`

/**
 * The filter half of a command-history request — everything except paging.
 *
 * Shared by the list and the export so the spreadsheet can only ever describe
 * the same rows the table is showing. Empty filters are omitted rather than
 * sent as empty arrays: absent means "all".
 */
const buildCmdHistoryFilters = (body) => {
  const meters = body?.meters ?? []
  const caNo = body?.ca_no ?? []
  const command = Array.isArray(body?.command) ? body.command : body?.command ? [body.command] : []
  const executionStatus = Array.isArray(body?.execution_status) ? body.execution_status : body?.execution_status ? [body.execution_status] : []
  const username = body?.username ?? ""

  return {
    ...(meters.length > 0 && { meters }),
    ...(caNo.length > 0 && { ca_no: caNo }),
    ...(command.length > 0 && { command }),
    ...(executionStatus.length > 0 && { execution_status: executionStatus }),
    ...(username && { username }),
    // Same spelling and ISO shape as `fota-history` on this service.
    ...(body?.start_time && { start_time: body.start_time }),
    ...(body?.end_time && { end_time: body.end_time })
  }
}

/**
 * Command history (new API): POST /api/v1/commands/history
 *
 * Query: `cursor` + `limit` — paging is not read from the body.
 * Body: meters, ca_no, command, execution_status, username, start_time and
 * end_time — each sent only when set, since absent means "all".
 * Response: { response_code, data: [...], pagination: { next, previous } }
 */
export const getCmdHistory = (body, cancelApi) => {
  const cancelToken = cancelApi ? createCancelToken(cancelApi) : undefined

  // Paging rides on the query string, not in the body — the endpoint reads
  // `cursor` and `limit` there and ignores them anywhere else, which left
  // every page serving the first one. The body carries filters only.
  const params = {
    ...(body?.cursor ? { cursor: body.cursor } : {}),
    ...(body?.limit != null ? { limit: body.limit } : {})
  }

  return tokenInstance.post(CMD_HISTORY_URL, buildCmdHistoryFilters(body), {
    params,
    cancelToken
  })
}

/**
 * The same endpoint and the same filters, with `download: true` — the server
 * answers with a spreadsheet instead of JSON, so the body is read as a blob.
 *
 * `cursor` and `limit` are deliberately not sent: an export covers the whole
 * filtered set, not the page currently on screen.
 */
export const downloadCmdHistory = (body, cancelApi) => {
  const cancelToken = cancelApi ? createCancelToken(cancelApi) : undefined
  const requestBody = { ...buildCmdHistoryFilters(body), download: true }
  return tokenInstance.post(CMD_HISTORY_URL, requestBody, {
    cancelToken,
    responseType: "blob"
  })
}

// -----------------------------------------------------------------------------
// Reports
// -----------------------------------------------------------------------------

const REPORTS_URL = `${api_url.thinkGasHes}/api/v1/reports`

/**
 * Every report Avdhaan can produce: GET /hes/api/v1/reports/catalog
 *
 * Each entry names the report, describes it, and — the part that matters to a
 * caller — lists exactly which `filters` it accepts, which column its dates
 * apply to, whether a date range is mandatory, and the widest span it allows.
 * A download is assembled against that list rather than against whatever a
 * screen happens to hold, which is why this is fetched once and kept.
 *
 * The documented call is a GET carrying `{}` as a body. A browser can't
 * reliably send one and the endpoint doesn't need it, so this is a plain GET.
 */
export const getReportsCatalog = (body = {}, cancelApi) => {
  const cancelToken = cancelApi ? createCancelToken(cancelApi) : undefined
  return tokenInstance.get(`${REPORTS_URL}/catalog`, { ...body, cancelToken })
}

/**
 * Ask for one: POST /hes/api/v1/reports/downloads
 *
 * Body is `{ report_name, filters }`, where `filters` holds only the keys the
 * catalog says that report takes.
 *
 * This does not return a file. It queues the job and answers `202` with
 * `{ data: { request_id, status } }` — the sheet is built on the server and
 * collected later, which is why the notification card calls this a *request*
 * and why `getReportDownloads` exists at all.
 */
export const downloadReport = (body, cancelApi) => {
  const cancelToken = cancelApi ? createCancelToken(cancelApi) : undefined
  const { report_name: reportName, filters = {} } = body ?? {}
  return tokenInstance.post(
    `${REPORTS_URL}/downloads`,
    { report_name: reportName, filters },
    { cancelToken }
  )
}

/**
 * Every report this user has asked for: GET /hes/api/v1/reports/downloads
 *
 * Same path as the POST, different method and an entirely different job: this
 * is the queue, and it is what the notification panel shows. Each row carries
 * the request id the POST handed back, and a status that moves on its own as
 * the server works through it.
 */
export const getReportDownloads = (body = {}, cancelApi) => {
  const cancelToken = cancelApi ? createCancelToken(cancelApi) : undefined
  return tokenInstance.get(`${REPORTS_URL}/downloads`, { ...body, cancelToken })
}

/**
 * Where to collect a finished one: GET /reports/downloads/:id/link
 *
 * Asked for at the moment someone presses the card rather than stored against
 * the row: these links are presigned and expire, so one fetched when the list
 * loaded would be stale by the time it was used.
 */
export const getReportDownloadLink = (requestId, cancelApi) => {
  const cancelToken = cancelApi ? createCancelToken(cancelApi) : undefined
  return tokenInstance.get(`${REPORTS_URL}/downloads/${requestId}/link`, { cancelToken })
}

/**
 * FOTA history (new API): POST /hes/api/v1/commands/fota-history
 *
 * Body: `{ meters: [<int>], execution_status: [...], start_time, end_time }`,
 * every key optional. Timestamps are "YYYY-MM-DDTHH:mm:ss".
 *
 * Response: flat `data` array with an opaque `pagination.next` token. Paging
 * follows its sibling `/commands/history` and travels in the body.
 */
/**
 * FOTA history: POST /api/v1/commands/fota-history
 *
 * The filters travel in the body; `limit` and `cursor` are query parameters,
 * the same split the command-history call above uses. They were in the body
 * too, which the endpoint ignores — so every page came back as page one.
 */
export const getFotaHistory = (body, cancelApi) => {
  const cancelToken = createCancelToken(cancelApi)
  const { data, params } = body ?? {}
  return tokenInstance.post(`${api_url.thinkGasHes}/api/v1/commands/fota-history`, data ?? {}, {
    params: params || {},
    cancelToken
  })
}

export const getGasMeterCommandHistoryData = (body, cancelApi) => {
  const cancelToken = createCancelToken(cancelApi)
  const { data, params } = body
  const requestBody = {
    meter: data?.meter || [],
    ca_no: data?.ca_no || []
  }
  const urlParams = params || {}
  return tokenInstance.post(`${api_url.thinkGasHes}/api/hes/gas/command-history/`, requestBody, { params: urlParams, cancelToken })
}

export const getGasMeterRechargesData = (body, cancelApi) => {
  body["cancelToken"] = createCancelToken(cancelApi)
  return tokenInstance.get(`${api_url.thinkGasHes}/api/hes/gas/recharges-data/`, body)
}

/**
 * Recharge history (new API): GET /hes/api/v1/accounts/recharges
 *
 * Keyed by `ca_no`, not by meter. Params: `ca_no`, `limit`, and whatever the
 * cursor in `pagination.next` carries.
 *
 * Response: `{ response_code, data: [...], pagination: { next, previous } }` —
 * a flat array, no `result` envelope and no total count.
 */
export const getAccountRecharges = (body, cancelApi) => {
  const cancelToken = createCancelToken(cancelApi)
  const { params } = body ?? {}
  return tokenInstance.get(`${api_url.thinkGasHes}/api/v1/accounts/recharges`, {
    params: params ?? {},
    cancelToken
  })
}

/**
 * The same recharges, as a spreadsheet: `download: true` on the same route.
 *
 * Follows `downloadCmdHistory` — one endpoint serving JSON or a file. `cursor`
 * and `limit` are deliberately not sent: an export covers the whole filtered
 * set for the account, not the page on screen.
 *
 * NOTE: the `download` flag is inferred from the command-history endpoint's
 * contract, not documented for this one. If the server ignores it the response
 * is JSON, which lands as a `.json` file rather than a spreadsheet — visible,
 * but wrong. Worth confirming with the API owner.
 */
export const downloadAccountRecharges = (body, cancelApi) => {
  const cancelToken = createCancelToken(cancelApi)
  const { params } = body ?? {}
  return tokenInstance.get(`${api_url.thinkGasHes}/api/v1/accounts/recharges`, {
    params: { ...(params ?? {}), download: true },
    cancelToken,
    responseType: "blob"
  })
}

/**
 * Reconciliation history (new API): GET /hes/api/v1/accounts/reconciliations
 *
 * Same contract as `getAccountRecharges` — keyed by `ca_no`, `limit` for page
 * size, flat `data` array and cursor-based `pagination`.
 */
export const getAccountReconciliations = (body, cancelApi) => {
  const cancelToken = createCancelToken(cancelApi)
  const { params } = body ?? {}
  return tokenInstance.get(`${api_url.thinkGasHes}/api/v1/accounts/reconciliations`, {
    params: params ?? {},
    cancelToken
  })
}

/**
 * Consumption history (new API): POST /hes/api/v1/reports/daily-readings
 *
 * One row per meter per day. A POST, unlike its `/accounts/*` siblings — a GET
 * here answers 405.
 *
 * Filters travel in the body: `{ ca_no: [...] }` or `{ meters: [...] }` as
 * **lists** (a bare string is a 422), plus `start_time` / `end_time` as
 * "YYYY-MM-DD HH:mm:ss" strings. Paging stays on the query string (`limit`),
 * and the response is the usual flat `data` array with an opaque
 * `pagination.next` token.
 *
 * Unrecognised body keys are accepted and ignored rather than rejected, so a
 * wrong filter name fails silently — it won't surface as an error.
 */
export const getDailyReadings = (body, cancelApi) => {
  const cancelToken = createCancelToken(cancelApi)
  const { params, data } = body ?? {}
  return tokenInstance.post(
    `${api_url.thinkGasHes}/api/v1/reports/daily-readings`,
    data ?? {},
    { params: params ?? {}, cancelToken }
  )
}

/**
 * Gas price history (new API): GET /hes/api/v1/gas-prices/history
 *
 * Query params are `ca_no`, `limit` and `cursor`; `project` is a **header**
 * here rather than a query param, and the axios instance already sends it.
 *
 * `ca_no` is optional — omitting it returns every meter's price history, not
 * this meter's, so it always has to be supplied.
 */
export const getGasPriceHistory = (body, cancelApi) => {
  const cancelToken = createCancelToken(cancelApi)
  const { params } = body ?? {}
  return tokenInstance.get(`${api_url.thinkGasHes}/api/v1/gas-prices/history`, {
    params: params ?? {},
    cancelToken
  })
}

/**
 * EMI schedule (new API): GET /hes/api/v1/accounts/emi/schedule
 *
 * Replaces the legacy `/api/ble/gas/emi-schedule/`. The param changed - `ca_no`,
 * not `ca_number` - and so did the envelope: the schedule is `data`, where the
 * legacy one nested it under `data.result`. `parseEmiScheduleResponse` reads
 * both; it was assumed unchanged here, and the tab drew zeros because of it.
 */
export const getGasEmiSchedule = (body, cancelApi) => {
  const cancelToken = createCancelToken(cancelApi)
  const { params } = body ?? {}
  return tokenInstance.get(`${api_url.thinkGasHes}/api/v1/accounts/emi/schedule`, {
    params: params ?? {},
    cancelToken
  })
}

/**
 * Log types accepted by `getMeterLogs`, in dropdown order.
 *
 * There is no endpoint listing these — the legacy `/api/hes/gas/log-list/` now
 * 404s — so the set is pinned here. The values are the enum the API validates
 * against: anything else comes back 422.
 */
export const METER_LOG_TYPES = [
  { label: "Communication Logs", value: "communication" },
  { label: "Tamper Logs", value: "tamper" },
  { label: "Relay Control Logs", value: "relayControl" },
  { label: "Other Event Logs", value: "otherEvent" },
  { label: "Transition Logs", value: "transition" }
]

/**
 * Meter logs (new API): POST /hes/api/v1/meter-logs
 *
 * `meter_id`, `limit`, `cursor` and `project` are query params; `log_type` is a
 * **required** body field (422 without it) and `start_time` / `end_time` are
 * optional body bounds as "YYYY-MM-DD HH:mm:ss".
 *
 * Response: flat `data` array with an opaque `pagination.next` token.
 */
export const getMeterLogs = (body, cancelApi) => {
  const cancelToken = createCancelToken(cancelApi)
  const { params, data } = body ?? {}
  return tokenInstance.post(`${api_url.thinkGasHes}/api/v1/meter-logs`, data ?? {}, {
    params: params ?? {},
    cancelToken
  })
}

export const getCustomerProfileData = (body, cancelApi) => {
  body["cancelToken"] = createCancelToken(cancelApi)
  return tokenInstance.get(`${api_url.thinkGasHes}/api/hes/gas/customer-profile/`, body)
}

/**
 * Customer profile (new API): GET /hes/api/v1/customers/profile?ca_no=…
 *
 * Keyed by CA number, not by meter. The profile sits directly on `data` —
 * there's no `result` envelope:
 *   `{ response_code, data: { name_of_customer, ca_no, … } }`
 *
 * A miss is a 404 carrying `{ error_message, details: { ca_no } }` at the top
 * level rather than inside `data`.
 *
 * NOTE: no trailing slash on this one — unlike the `/gas/*` routes. Getting it
 * wrong costs a 307, which a browser can't follow on a preflighted CORS
 * request even though curl and Postman handle it silently.
 */
export const getCustomerProfileV1 = (body, cancelApi) => {
  const cancelToken = createCancelToken(cancelApi)
  const { params } = body ?? {}
  return tokenInstance.get(`${api_url.thinkGasHes}/api/v1/customers/profile`, {
    params: params ?? {},
    cancelToken
  })
}

/**
 * Latest configuration read off a meter: GET /hes/api/v1/meters/latest-configuration
 *
 * Both `ca_no` and `meter_id` are required, and `meter_id` is the bare integer
 * — the printed "PG…" serial fails validation, same as the other v1 routes.
 *
 * The payload groups the settings into objects (`network_configuration`,
 * `communication_schedule`, …) beside a few flat identity fields.
 *
 * No trailing slash, matching the other `/api/v1` routes here.
 */
export const getMeterLatestConfiguration = (body, cancelApi) => {
  const cancelToken = createCancelToken(cancelApi)
  const { params } = body ?? {}
  return tokenInstance.get(`${api_url.thinkGasHes}/api/v1/meters/latest-configuration`, {
    params: params ?? {},
    cancelToken
  })
}

/**
 * Meter history for a CA number: GET /hes/api/v1/customers/meter-history
 *
 * Answers `{ current_meter, past_meters[] }` — the meter in service now and
 * every meter it replaced. Keyed by `ca_no` alone; the meter in the URL is
 * irrelevant, since the point is to list the others.
 *
 * No trailing slash, same as `/customers/profile` above.
 */
export const getCustomerMeterHistory = (body, cancelApi) => {
  const cancelToken = createCancelToken(cancelApi)
  const { params } = body ?? {}
  return tokenInstance.get(`${api_url.thinkGasHes}/api/v1/customers/meter-history`, {
    params: params ?? {},
    cancelToken
  })
}

/**
 * Commissioning details: GET /hes/api/v1/gas/commissioning-details
 *
 * Both query params are required: `ca_no` as a string and `meter_id` as the
 * bare integer, without the "PG" prefix the printed serial carries.
 *
 * Answers the commissioning steps *and* every meter photo in one call,
 * straight on `data`:
 *   `{ meter_id, polaris_meter_id, commissioning_steps[], meter_images_time,
 *      move_in_data_time, front_image, bottom_image, left_image, right_image,
 *      top_image, move_in_reading_image, last_reading_image, burner_image }`
 *
 * It replaces `/gas/latest-meter-images/`, which is gone. The steps used to
 * ride along on `/customers/profile`; that endpoint still serves the rest of
 * the customer record but no longer the steps.
 *
 * No trailing slash on this one, unlike the other `/gas/*` routes.
 */
export const getCommissioningDetails = (body, cancelApi) => {
  const cancelToken = createCancelToken(cancelApi)
  const { params } = body ?? {}
  return tokenInstance.get(`${api_url.thinkGasHes}/api/v1/gas/commissioning-details`, {
    params: params ?? {},
    cancelToken
  })
}

// Download 
export const getdownloadData = (body, cancelApi) => {
  const cancelToken = createCancelToken(cancelApi)
  const { data } = body
  // Request body is already constructed in the component, use it directly
  return tokenInstance.post(`${api_url.prodModule}download/oqc-data/`, data, { cancelToken })
}

/**
 * Download history.
 *
 * `body` is forwarded now — it used to be accepted and dropped, so the `page`
 * the modal tracked never reached the server and every page showed the first
 * ten rows. The response has carried `pagination.total_rows` all along.
 */
export const getdownloadDataList = (body, cancelApi) => {
  const cancelToken = createCancelToken(cancelApi)
  return tokenInstance.get(`${api_url.prodModule}download-list/`, {
    ...body,
    cancelToken
  })
}
